import { useEffect, useState, type ReactNode } from "react";
import type { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import { vars } from "@pack-it-up/ui-web";
import { DragHandleIcon } from "../../app/icons.js";

// Touch/coarse-pointer devices (mobile browsers): dnd-kit's pointer-drag doesn't work
// reliably there, and the handle is a fussy target to begin with, so it's just dead
// chrome. Hidden rather than shipped-but-broken.
const useCoarsePointer = (): boolean => {
  const query = "(pointer: coarse)";
  const [coarse, setCoarse] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const onChange = (): void => setCoarse(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return coarse;
};

// Visual drag-handle affordance (2×3 dot grid). When `listeners` and `attributes`
// are passed (from useSortable), the handle becomes an interactive drag activator.
// When omitted (non-manual sort or viewer), it renders as a purely decorative element
// (cursor: default, not tabbable).
export const DragHandle = ({
  listeners,
  attributes,
}: {
  listeners?: DraggableSyntheticListeners;
  attributes?: DraggableAttributes;
}): ReactNode => {
  const coarse = useCoarsePointer();
  if (coarse) return null;
  return (
    <span
      style={{ color: vars.color.textMuted, cursor: listeners ? "grab" : "default", flexShrink: 0, display: "inline-flex", position: "relative", top: "2px" }}
      {...(listeners ?? {})}
      {...(attributes ?? {})}
      // Accessible name for the activator (dnd-kit's attributes give it role="button").
      {...(listeners !== undefined ? { "aria-label": "Drag to reorder" } : {})}
    >
      {DragHandleIcon}
    </span>
  );
};
